import React, { useEffect, useState } from "react";
import { Tooltip, Alert } from "@mui/material";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import AddCircleOutlineIcon from "@mui/icons-material/AddCircleOutline";
import handleService from "../../services/apiService.ts";
import { MethodsEnum } from "../../enums/Methods.ts";
import { Link } from "react-router-dom";
import { useFeedback } from "../../contexts/FeedbackContext.tsx";
import { usePlanoContext } from "../../contexts/PlanoContext.tsx";
import { formatBrazilPhone } from "../../utils/phone.util.ts";
import { isCreator } from "../../utils/admin.util.ts";
import { getPlanLimits } from "../../utils/planLimits.util.ts";
import { usePlanLimits } from "../../hooks/usePlanLimits.ts";
import CreateUsuarioDrawer from "../../components/Modals/CreateUsuarioDrawer/index.tsx";
import UpdateUsuarioDrawer from "../../components/Modals/UpdateUsuarioDrawer/index.tsx";
import ConfirmDeleteDialog from "../../components/Modals/ConfirmDeleteDialog/index.tsx";
import { Response } from "../../types/Api.ts";
import Button from "../../ui/Button.tsx";
import SearchField from "../../ui/SearchField.tsx";
import SkeletonRows from "../../ui/SkeletonRows.tsx";
import EmptyState from "../../ui/EmptyState.tsx";
import "../../styles/design-system.css";
import { TipoUsuario } from "../../types/Permissoes.ts";

interface Usuario {
  id: string;
  email: string;
  telefone: string;
  nome?: string | null;
  tipo?: TipoUsuario;
  tipoRotulo?: string;
  podeExcluirAgendamento?: boolean;
  podeVerLog?: boolean;
}

/**
 * Gestao de usuarios da empresa. Vive como componente, e nao como pagina, para
 * ser montada dentro da aba "Usuarios" de Configuracoes - era uma tela solta no
 * menu lateral antes disso.
 *
 * Quem monta e responsavel por so fazer isso para ADM: montar ja dispara
 * GET /usuario/listar.
 */
/**
 * Cor por função, para dar pra bater o olho e saber quem é quem — antes as três
 * saíam com o mesmo badge sem cor, indistinguíveis.
 */
const CORES_DA_FUNCAO: Record<string, string> = {
  ADM: "ds-badge-blue",
  OPERADOR: "ds-badge-green",
  INSTRUMENTADOR: "ds-badge-neutral",
};

const GestaoUsuarios = () => {
  const [usuarios, setUsuarios] = useState<Usuario[]>([]);
  const [filtroEmail, setFiltroEmail] = useState("");
  const [isLoading, setIsLoading] = useState(true);
  const [isExcluindo, setIsExcluindo] = useState(false);
  const [isCreateOpen, setIsCreateOpen] = useState(false);
  const [isEditOpen, setIsEditOpen] = useState(false);
  const [currentUsuario, setCurrentUsuario] = useState<Usuario | null>(null);
  const [usuarioParaExcluir, setUsuarioParaExcluir] = useState<Usuario | null>(null);

  const { showFeedback } = useFeedback();
  // isPlanActive é a mesma fonte de verdade usada por ProtectedRoute e por
  // "Minha Assinatura" — considera dataExpiracao (igual o AcessoBloqueadoInterceptor
  // do backend), não o campo "pago". "pago" só vira true quando a ASAAS confirma
  // um pagamento de verdade; contas liberadas manualmente pelo painel admin
  // (alterarPlano/alterarDataLiberacao) nunca passam por ali e ficam com
  // pago=false para sempre, mesmo com acesso real e válido — usar "pago === false"
  // como sinônimo de "inativo" (bug antigo desta página) bloqueia essas contas
  // incorretamente.
  const { planoNome, isPlanActive, refreshPlano } = usePlanoContext();
  const { limits: planLimits } = usePlanLimits();

  useEffect(() => {
    fetchUsuarios();
    refreshPlano();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const userCount = usuarios.length;
  const limite = planLimits?.limiteUsuarios ?? getPlanLimits(planoNome).limite;
  const isLimitReached = limite > 0 && userCount >= limite;
  const isRestricted = !isPlanActive;

  const fetchUsuarios = async () => {
    try {
      setIsLoading(true);
      const response = await handleService({ endpoint: "/usuario/listar" });
      const lista: Usuario[] = Array.isArray(response) ? response : response.data || [];
      setUsuarios(lista);
    } catch (error) {
    } finally {
      setIsLoading(false);
    }
  };

  const usuariosFiltrados = usuarios.filter((usuario) =>
    usuario.email.toLowerCase().includes(filtroEmail.toLowerCase())
  );

  const handleEditar = (usuario: Usuario) => {
    setCurrentUsuario(usuario);
    setIsEditOpen(true);
  };

  const handleCloseEdit = () => {
    setIsEditOpen(false);
    setCurrentUsuario(null);
  };

  const onCreated = () => {
    setIsCreateOpen(false);
    fetchUsuarios();
  };

  const onUpdate = (response: Response<Usuario>) => {
    const usuario = response.data;
    if (usuario) {
      setUsuarios((prev) => prev.map((u) => (u.id === usuario.id ? usuario : u)));
      setIsEditOpen(false);
    }
  };

  const handleConfirmarExclusao = async () => {
    if (!usuarioParaExcluir) return;
    try {
      setIsExcluindo(true);
      await handleService({ endpoint: `/usuario/${usuarioParaExcluir.id}`, method: MethodsEnum.DELETE });
      setUsuarios((prev) => prev.filter((u) => u.id !== usuarioParaExcluir.id));
      showFeedback("Usuário excluído com sucesso!", "success");
      setUsuarioParaExcluir(null);
    } catch (error: any) {
      showFeedback(error.message || "Erro ao excluir usuário", "error");
    } finally {
      setIsExcluindo(false);
    }
  };

  return (
    <div className="ds">
      {/*
        Sem ds-page-head aqui: o titulo da pagina e "Configuracoes", e esta e
        uma aba dentro dela. Repetir um h1 daria dois titulos de pagina na
        mesma tela.
      */}
      <div
        style={{
          display: "flex",
          alignItems: "flex-start",
          justifyContent: "space-between",
          gap: 16,
          flexWrap: "wrap",
          marginBottom: 16,
        }}
      >
        <div>
          <div className="ds-section-title" style={{ marginBottom: 2 }}>Usuários da empresa</div>
          <p style={{ fontSize: 12.6, color: "var(--ink-faint)", margin: 0 }}>
            Quem tem acesso ao sistema e o que cada um pode fazer.
          </p>
        </div>
        <div>
          <Tooltip title={
            isLimitReached && limite > 0
              ? `Limite de ${limite} usuários do plano atingido. Faça upgrade para adicionar mais.`
              : isRestricted
              ? "Assinatura inativa. Renove seu plano para gerenciar usuários."
              : ""
          }>
            <span>
              <Button
                variant="primary"
                icon={<AddCircleOutlineIcon fontSize="small" />}
                onClick={() => setIsCreateOpen(true)}
                disabled={isLimitReached || isRestricted}
              >
                Novo usuário
              </Button>
            </span>
          </Tooltip>
        </div>
      </div>

      {isLimitReached && (
        <Alert severity="warning" style={{ marginBottom: 16 }}>
          Limite de {limite} usuários do plano atingido.{" "}
          <Link to="/planos/listagem" style={{ fontWeight: "bold" }}>Faça upgrade</Link> para adicionar mais usuários.
        </Alert>
      )}
      {isRestricted && (
        <Alert severity="error" style={{ marginBottom: 16 }}>
          Assinatura inativa. Acesse <Link to="/meu-plano" style={{ fontWeight: "bold" }}>Meu Plano</Link> para regularizar seu acesso.
        </Alert>
      )}

      <div className="ds-toolbar">
        <SearchField value={filtroEmail} onChange={setFiltroEmail} placeholder="Buscar por e-mail..." />
      </div>

      <div className="ds-table-wrap">
        <table className="ds-table">
          <thead>
            <tr>
              <th>Nome</th>
              <th>Email</th>
              <th>Função</th>
              <th>Telefone</th>
              <th>Ações</th>
            </tr>
          </thead>
          <tbody>
            {isLoading ? (
              <SkeletonRows columns={5} />
            ) : usuariosFiltrados.length === 0 ? (
              <tr>
                <td colSpan={5}>
                  <EmptyState title="Nenhum usuário encontrado" />
                </td>
              </tr>
            ) : (
              usuariosFiltrados.map((usuario) => {
                const isProprioUsuario = isCreator(usuario.id.toString());
                const acoesDesabilitadas = isProprioUsuario || isRestricted;
                const tooltipAcoes = isProprioUsuario
                  ? "Você não pode editar/excluir sua própria conta por aqui"
                  : isRestricted
                    ? "Assinatura inativa"
                    : "";
                return (
                  <tr key={usuario.id}>
                    <td className="ds-cell-title">{usuario.nome || "—"}</td>
                    <td>{usuario.email}</td>
                    <td>
                      <div style={{ display: "flex", alignItems: "center", gap: 6, flexWrap: "wrap" }}>
                        <span
                          className={`ds-badge ds-badge-sem-ponto ${
                            CORES_DA_FUNCAO[usuario.tipo || ""] || "ds-badge-neutral"
                          }`}
                        >
                          {usuario.tipoRotulo || usuario.tipo || "—"}
                        </span>

                        {/*
                          As duas permissões individuais viviam só num title —
                          um tooltip que ninguém descobre. Sem elas na linha, não
                          havia como ver quem pode apagar cirurgia sem abrir
                          usuário por usuário.
                        */}
                        {usuario.podeExcluirAgendamento && (
                          <span
                            className="ds-badge ds-badge-sem-ponto ds-badge-orange"
                            title="Pode excluir cirurgias definitivamente"
                          >
                            excluir agendamento
                          </span>
                        )}
                        {usuario.podeVerLog && (
                          <span
                            className="ds-badge ds-badge-sem-ponto ds-badge-teal"
                            title="Pode ver o log de atividades"
                          >
                            ver log
                          </span>
                        )}
                      </div>
                    </td>
                    <td>{formatBrazilPhone(usuario.telefone) || usuario.telefone || "—"}</td>
                    <td>
                      <div className="ds-row-actions">
                        <Tooltip title={tooltipAcoes}>
                          <span>
                            <button
                              className="ds-icon-btn"
                              onClick={() => handleEditar(usuario)}
                              disabled={acoesDesabilitadas}
                              title="Editar"
                            >
                              <EditOutlinedIcon fontSize="small" />
                            </button>
                          </span>
                        </Tooltip>
                        <Tooltip title={tooltipAcoes}>
                          <span>
                            <button
                              className="ds-icon-btn"
                              onClick={() => setUsuarioParaExcluir(usuario)}
                              disabled={acoesDesabilitadas}
                              title="Excluir"
                            >
                              <DeleteOutlineIcon fontSize="small" />
                            </button>
                          </span>
                        </Tooltip>
                      </div>
                    </td>
                  </tr>
                );
              })
            )}
          </tbody>
        </table>
      </div>

      <CreateUsuarioDrawer open={isCreateOpen} onClose={() => setIsCreateOpen(false)} onCreated={onCreated} />
      <UpdateUsuarioDrawer isOpen={isEditOpen} onClose={handleCloseEdit} usuario={currentUsuario} onUpdate={onUpdate} />

      <ConfirmDeleteDialog
        open={!!usuarioParaExcluir}
        title="Excluir Usuário"
        message="Tem certeza que deseja excluir este usuário? Ele perderá o acesso ao sistema imediatamente. Esta ação não poderá ser desfeita."
        loading={isExcluindo}
        onCancel={() => setUsuarioParaExcluir(null)}
        onConfirm={handleConfirmarExclusao}
      />
    </div>
  );
};

export default GestaoUsuarios;
